import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import {
  ShieldAlert,
  Activity,
  Fingerprint,
  Globe,
  Smartphone,
  CreditCard,
  UserCheck,
  Flag,
  ListChecks
} from 'lucide-react';
import EmbeddedModule from './EmbeddedModule';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LENS_ICONS = {
  behavioral: Activity,
  kyc: UserCheck,
  sanctions: Flag,
  whitelist: ListChecks,
  'cross-border': Globe,
  upi: CreditCard,
  device: Fingerprint,
  geo: Globe
};

const FRMGuardrail = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/frm/metrics`)
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, []);

  const totalAlerts = data?.lenses.reduce((a, l) => a + l.alerts_24h, 0) || 0;
  const totalBlocked = data?.lenses.reduce((a, l) => a + l.blocked, 0) || 0;

  return (
    <EmbeddedModule
      eyebrow="Anti-Fraud · Real-time"
      title="FRM Guardrail"
      tagline="Multi-lens anti-fraud guardrail — behavioural, KYC, sanctions, whitelist, cross-border, UPI/channel, device fingerprint and geo signals. Real-time decisions on UPI links, mobile-number links, bank-backend identifiers, KYC numbers and locational graphs."
      badgeText="Bank-grade · Sub-second"
      accentClass="from-rose-700 via-rose-600 to-red-700"
      embedUrl="https://frm-guard.lovable.app"
      embedTitle="frm-guard.lovable.app"
      testId="frm-guardrail-page"
      kpis={[
        { label: 'Alerts (24h)', value: totalAlerts.toLocaleString() },
        { label: 'Blocked / Killed', value: totalBlocked },
        { label: 'Decision Latency', value: data ? `${data.decision_latency_ms} ms` : '—' },
        { label: 'TP Rate / FP Rate', value: data ? `${data.true_positive_rate} / ${data.false_positive_rate}` : '—' }
      ]}
    >
      <Card data-testid="frm-lenses">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <ShieldAlert className="w-5 h-5 text-rose-600" />
            <span>Detection Lenses</span>
            <Badge variant="secondary">{data?.lenses.length || 0}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {data?.lenses.map((lens) => {
              const Icon = LENS_ICONS[lens.key] || ShieldAlert;
              const trendUp = lens.trend.startsWith('+');
              return (
                <div
                  key={lens.key}
                  data-testid={`lens-${lens.key}`}
                  className="border border-gray-200 rounded-lg p-4 bg-white hover:border-rose-300 hover:shadow-sm transition-all"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="w-8 h-8 rounded-md bg-rose-50 border border-rose-100 flex items-center justify-center">
                      <Icon className="w-4 h-4 text-rose-700" />
                    </div>
                    <Badge
                      variant="outline"
                      className={`text-[10px] ${trendUp ? 'text-rose-700 border-rose-200' : 'text-emerald-700 border-emerald-200'}`}
                    >
                      {lens.trend}
                    </Badge>
                  </div>
                  <p className="text-xs uppercase tracking-wider text-gray-500">{lens.label}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{lens.alerts_24h}</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    <span className="font-semibold text-rose-700">{lens.blocked}</span> blocked
                  </p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Real-time Signals Tracked</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {[
              'UPI-link velocity',
              'Mobile-number link clusters',
              'Bank backend ID drift',
              'KYC number reuse',
              'Locational anomaly',
              'Geo-graph proximity',
              'Device fingerprint',
              'IMEI / SIM swap',
              'Sanctions list match',
              'Mule-account topology',
              'Cross-border corridor risk',
              'Channel switching cadence'
            ].map((s) => (
              <Badge key={s} variant="outline" className="text-xs">
                {s}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>
    </EmbeddedModule>
  );
};

export default FRMGuardrail;
